<template>
  <div v-if="recipient">
    <div class="the_survey">
      <div class="survey_header">
        <h1 v-if="preview">Previewing Survey</h1>
        <figure
          class="survey-company-logo"
          v-if="survey.svg_logo"
          v-html="survey.svg_logo"
        ></figure>
      </div>
      <div v-if="expired" class="centralize">
        <h3>This survey is no longer active</h3>
        <div class="survey-expired-text" v-html="survey.expired_text" />
      </div>
      <div v-else>
        <SlideDots :total="slideCount" :current="currentSlide" />
        <div class="questions" id="questions" ref="questions">
          <!-- Start of slides iteration -->
          <div
            class="section"
            :class="{ 'survey-question-section': type === 'SurveyQuestion' }"
            v-for="({ type, question }, i) in slides"
            :key="`section-${i}`"
            :data-question-id="question?.id"
          >
            <div v-if="type === 'Title'" class="content">
              <h2>{{ survey.name }}</h2>
              <div v-html="introText" class="mb-4" />
              <div class="regular-controls">
                <b-button @click="agreeAndContinue" variant="primary">
                  {{ showInterstital ? 'Agree and take survey' : 'Take survey' }}
                </b-button>
              </div>
              <div class="disclaimer">
                UserEvidence is a survey research firm, used by vendors like
                {{ account.name }} to gain valuable insights from their users. If results of survey
                are published, your responses will be anonymous, unless you explicitly agree to
                share your name. UserEvidence will not share your contact information with
                3rd-parties. View our
                <a :href="privacyPageUrl" target="blank">Privacy Policy</a>
              </div>
            </div>
            <div v-else-if="type === 'EndSurvey'" class="content">
              <h2 class="mb-3">Thank you for taking the survey!</h2>
              <b-button class="done-btn" variant="primary" @click="goDone">Done</b-button>
            </div>
            <div v-else class="content d-flex">
              <div class="question-number">
                <h3>{{ i }}.</h3>
              </div>
              <div class="flex-fill">
                <div v-if="type === 'CompanyQuestion'">
                  <h3 class="mb-4">About Your Company</h3>
                  <CompanyQuestion
                    :recipient="recipient"
                    :question-flags="companyQuestionFlags"
                    @recipient-updated="updateRecipient"
                  />
                </div>
                <div v-if="type === 'RecipientQuestion'">
                  <h3 class="mb-4">About You</h3>
                  <RecipientQuestion
                    :recipient="recipient"
                    :question-flags="recipientQuestionFlags"
                    @recipient-updated="updateRecipient"
                  />
                </div>
                <div v-if="type === 'SurveyQuestion'">
                  <div class="question">
                    <h3 class="mb-4">
                      <span v-html="question.the_question" />
                      {{ question.required ? ' *' : '' }}
                    </h3>
                    <MultipleChoiceMany
                      v-if="question.type == 'MultipleChoiceMany'"
                      :question="question"
                      @check="saveResponse"
                      @uncheck="removeResponse"
                    />
                    <MultipleChoiceOne
                      v-else-if="question.type == 'MultipleChoiceOne'"
                      :question="question"
                      @change="saveResponse"
                    />
                    <MultipleChoiceOne
                      v-else-if="question.type == 'NamedAttribution'"
                      :question="namedAttributionQuestion"
                      @change="saveResponse"
                    />
                    <Nps
                      v-else-if="question.type == 'Nps'"
                      :question="question"
                      @change="saveResponse"
                    />
                    <Testimonial
                      v-else-if="question.type == 'Testimonial'"
                      :question="question"
                      @change="saveResponse"
                    />
                    <MultipleChoiceMany
                      v-else-if="question.type == 'ReferenceVolunteer'"
                      :question="question"
                      @check="saveResponse"
                      @uncheck="removeResponse"
                    />
                    <Slider
                      v-else-if="question.type == 'Slider'"
                      :question="question"
                      @change="saveResponse"
                    />
                    <MatrixxQuestion
                      v-else-if="question.type == 'Matrixx'"
                      :question="question"
                      @change="saveResponse"
                    />
                    <MultipleChoiceOne
                      v-else-if="question.type == 'ClassifierMcoQuestion'"
                      :question="question"
                      @change="saveResponse"
                    />
                  </div>
                </div>
                <div class="regular-controls mt-2">
                  <ErrorMessages :errors="currentQuestionErrors" class="mb-2 validation-errors" />
                  <b-button variant="primary" @click="next"> Next </b-button>
                </div>
              </div>
            </div>
          </div>
          <!-- End of slides iteration -->
        </div>
        <div class="footer d-flex align-items-center">
          <div class="responsive-controls mt-1">
            <div class="w-100 d-flex flex-column">
              <ErrorMessages :errors="currentQuestionErrors" class="w-100 text-center mb-2" />
              <div class="w-100">
                <b-button v-if="currentSlide === 1" @click="agreeAndContinue" variant="primary">
                  {{ showInterstital ? 'Agree and take survey' : 'Take survey' }}
                </b-button>
                <b-button v-else-if="currentSlide < slideCount" variant="primary" @click="next">
                  Next
                </b-button>
                <b-button v-else variant="primary" @click="goDone">Done</b-button>
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center credits">
            <span class="mr-2">Powered by</span>
            <img src="@images/userevidence_logo.svg" alt="UserEvidence" />
          </div>
          <div class="nav_buttons d-flex align-items-center">
            <b-button @click="previous" variant="outline-secondary">
              <i class="bi bi-chevron-up" />
            </b-button>
            <b-button @click="next" variant="outline-secondary" class="ml-1">
              <i class="bi bi-chevron-down" />
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import * as api from '@/survey/api.js'
import { postResponse, patchResponse, deleteResponse } from '@app/api.js'
import appsignal from '@lib/appsignal'
import fullpage from 'fullpage.js'
import 'fullpage.js/dist/fullpage.css'

import MultipleChoiceMany from '@/survey/components/MultipleChoiceMany.vue'
import MultipleChoiceOne from '@/survey/components/MultipleChoiceOne.vue'
import Slider from '@/survey/components/SliderQuestion.vue'
import Nps from '@/survey/components/Nps.vue'
import Testimonial from '@/survey/components/Testimonial.vue'
import CompanyQuestion from '@/survey/components/CompanyQuestion.vue'
import RecipientQuestion from '@/survey/components/RecipientQuestion.vue'
import MatrixxQuestion from '@/survey/components/MatrixxQuestion.vue'
import SlideDots from '@/survey/components/SlideDots.vue'
import ErrorMessages from '@/survey/components/ErrorMessages.vue'
import { mapState } from 'pinia'
import { useQuestionStore } from '@app/stores/QuestionStore.ts'
import { useRecipientStore } from '@app/stores/RecipientStore.ts'
import { useSurveyResponseStore } from '@app/stores/SurveyResponseStore.ts'
import { useValidationStore } from '@/survey/stores/ValidationStore.ts'
import { reportError } from '@lib/errorHandler'
import SurveyQuestionLogician from '@lib/surveyQuestionLogician.ts'

/*
 * WARNING:
 * This component is using the fullPage.js library to present the survey questions one-by-one.
 * (The docs are here: https://alvarotrigo.com/fullPage/docs/)
 * The library documentation talks about "sections" and "slides" which have different APIs.
 * In this component, we are using "sections" only, but the variables refer to slides.
 * In other words, the `slides` array is a list of sections, not slides. Don't get confused!
 */
export default {
  props: {
    recipientToken: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      fullpageObject: null,
      slides: [], // List of slides - see `buildSlideList`
      currentSlide: 1, // Stores the current slide and starts at 1 because fullpage.js moveTo argument is 1-indexed
      survey: {},
      account: {},
      preview: Boolean(this.$route?.query?.preview),
      optin: false,
      initiallyShowCompanyQuestion: true,
      initiallyShowRecipientQuestion: true,
      // We need this 👇 to map the fullpage.js section index to our list of questions
      indexOfFirstQuestionSection: null,
      previewFakeResponseIds: new Map(), // Map to store fake IDs for preview mode so they can be persisted in the store
    }
  },
  components: {
    ErrorMessages,
    MultipleChoiceMany,
    MultipleChoiceOne,
    Nps,
    Testimonial,
    Slider,
    CompanyQuestion,
    RecipientQuestion,
    MatrixxQuestion,
    SlideDots,
  },
  async mounted() {
    let response
    if (this.preview && this.recipientToken) {
      // Preview Mode
      // Get survey preview data by survey token
      response = await api.getSurveyPreview(this.recipientToken)
    } else {
      // Normal Survey mode
      // Get survey data by recipient token
      response = await api.signInAndGetRecipient(this.recipientToken)
    }

    this.account = response.data.survey.account
    this.survey = response.data.survey

    this.upsertQuestionsAndAnswers(this.survey.questions)
    if (this.preview) {
      const recipient = {
        id: '1',
        token: this.recipientToken,
        survey_id: this.survey.id,
        first_name: 'Customer',
        title: 'Title',
        email: 'customer@userevidence.com',
        best_company_name: 'Company Name',
        has_company_info: false,
        has_personal_info: false,
        completed_at: null,
      }
      this.upsertRecipient(recipient)
    } else this.upsertRecipientAndResponses(this.survey.recipients[0])

    // check if it has already been completed
    if (this.surveyCompleted) {
      this.goToCompletePage()
      return
    }

    // we set this on mount so that the UI doesnt change as the questions
    // get answered
    this.initiallyShowCompanyQuestion = this.showCompanyQuestion
    this.initiallyShowRecipientQuestion = this.showRecipientQuestion

    // This must happen before this.setupScroll
    this.buildSlideList()

    // questions must be in DOM before initializing fullpage
    this.$nextTick(() => {
      this.setupScroll()
    })
  },
  computed: {
    ...mapState(useQuestionStore, ['upsertQuestionsAndAnswers']),
    ...mapState(useRecipientStore, [
      'recipients',
      'upsertRecipient',
      'upsertRecipientAndResponses',
    ]),
    ...mapState(useSurveyResponseStore, [
      'surveyResponses',
      'upsertSurveyResponse',
      'removeSurveyResponse',
    ]),
    initialQuestionIndex() {
      // Calculate a starting position if the recipient has answered a question,
      // otherwise we start at the first slide. If, for some reason, the recipient
      // has left a required question behind, we will start at that question.
      const destinationQuestion = [this.lastRequiredUnansweredQuestion, this.lastAnsweredQuestion]
        .filter((question) => question !== undefined)
        .sort((a, b) => a.position - b.position)[0]

      if (destinationQuestion) {
        // Get position of the question in the slides array
        return this.slides.findIndex((s) => s.question?.id == destinationQuestion.id)
      }
      return 0
    },
    recipientQuestionFlags() {
      // Toggles that determine which recipient questions are shown
      return {
        showRoleQuestion: this.survey.show_role_question,
        showTitleQuestion: this.survey.show_title_question,
        showSeniorityQuestion: this.survey.show_seniority_question,
      }
    },
    companyQuestionFlags() {
      // Toggles that determine which company questions are shown
      return {
        showCompanyNameQuestion: this.survey.show_company_name_question,
        showIndustryQuestion: this.survey.show_industry_question,
        showCompanySizeQuestion: this.survey.show_company_size_question,
      }
    },
    anyCompanyQuestionsVisible() {
      // Check if any of the 3 questions are visible
      return Object.values(this.companyQuestionFlags).some((flag) => flag)
    },
    anyRecipientQuestionsVisible() {
      // Check if any of the 3 questions are visible
      return Object.values(this.recipientQuestionFlags).some((flag) => flag)
    },
    showInterstital() {
      if (this.preview) return false
      return (
        !this.recipient.responded &&
        this.survey.custom_survey_landing_page_text?.length > 0 &&
        !this.optin
      )
    },
    introText() {
      return this.showInterstital
        ? this.survey.custom_survey_landing_page_text
        : this.survey.promotion_text
    },
    recipient() {
      return this.recipients.find((r) => r.token == this.recipientToken)
    },
    questions() {
      const sortedQuestions = this.survey?.questions
      return sortedQuestions.sort((a, b) => a.position - b.position)
    },
    namedAttributionQuestion() {
      var q = this.questions.filter((q) => q.type == 'NamedAttribution')[0]
      if (this.survey.custom_named_attribution) return q

      var name = 'Name'
      var title = 'Job Title'
      var company = 'Company Name'

      if (this.recipient.first_name && this.recipient.last_name)
        name = `${this.recipient.first_name} ${this.recipient.last_name}`
      if (this.recipient.best_company_name) company = this.recipient.best_company_name
      else if (this.recipient.company_name) company = this.recipient.company_name
      if (this.recipient.title) title = this.recipient.title

      q.the_question = `Are you willing to let ${this.account.name} use your name and company in published research? Here's how it would show up: "${name}, ${title}, ${company}"`
      return q
    },
    showCompanyQuestion() {
      if (this.survey.nps_survey) return false
      if (this.recipient.has_company_info) return false
      if (!this.anyCompanyQuestionsVisible) return false
      return true
    },
    showRecipientQuestion() {
      if (this.survey.nps_survey) return false
      if (this.recipient.has_personal_info) return false
      if (!this.anyRecipientQuestionsVisible) return false
      return true
    },
    expired() {
      return this.survey.expires_at && new Date(this.survey.expires_at) < new Date()
    },
    slideCount() {
      return this.slides?.length
    },
    answeredQuestions() {
      const allAnsweredQuestionIds = [
        ...new Set(this.surveyResponses.map(({ question_id }) => question_id)),
      ]

      return this.questions
        .map((q) => {
          if (allAnsweredQuestionIds.includes(q.id)) return q

          if (q.type == 'Matrixx') {
            const rowsIds = q.rows?.map((r) => r.id)
            if (rowsIds.every((id) => allAnsweredQuestionIds.includes(id))) return q
          }
        })
        .filter((question) => !!question)
        .sort((a, b) => a.position - b.position)
    },
    lastAnsweredQuestion() {
      return this.answeredQuestions[this.answeredQuestions.length - 1]
    },
    lastRequiredUnansweredQuestion() {
      // this is currently only relevant if there's at least 1 response
      if (this.surveyResponses.length == 0) return

      const answeredQuestionIds = this.answeredQuestions.map((q) => q.id)

      // We need to ignore required questions that are not being shown
      // to the user because they were actually conditional to another question.
      return this.questions
        .filter((q, index: number) => {
          return (
            q.required &&
            !answeredQuestionIds.includes(q.id) &&
            this.logician.evaluateCurrentQuestion(index + 1) // fullpage is 1-base index
          )
        })
        .sort((a, b) => a.position - b.position)[0]
    },
    surveyCompleted() {
      return this.recipient.completed_at
    },
    privacyPageUrl() {
      return `${process.env.VUE_APP_UE_WWW_URL}/privacy`
    },
    currentQuestion() {
      const slide = this.slides[this.currentSlide - 1]
      return slide?.question
    },
    currentQuestionErrors() {
      return useValidationStore().getErrors(this.currentQuestion?.id)
    },
    logician() {
      return new SurveyQuestionLogician(
        this.questions,
        this.surveyResponses,
        reportError,
        this.indexOfFirstQuestionSection,
      )
    },
  },
  methods: {
    goToCompletePage() {
      return (window.location.href = `/surveys/${this.recipient.token}/complete`)
    },
    // use this as opposed to the store updateRecipient method because we use
    // a different api method to update the recipient
    updateRecipient(recipient) {
      if (this.preview) return
      api
        .patchRecipient(recipient)
        .then((response) => this.upsertRecipient(response.data.recipient))
    },
    agreeAndContinue() {
      this.optin = true
      this.next()
    },
    getRandomId() {
      return Math.random().toString(36).substring(2, 9)
    },
    goDone() {
      if (!this.questionIsValid()) return

      // if there is a required question that has been left unanswered, jump to that question
      if (this.lastRequiredUnansweredQuestion) {
        const destinationSlideIndex = this.slides.findIndex(
          (s) => s.question?.id == this.lastRequiredUnansweredQuestion.id,
        )
        this.moveTo(destinationSlideIndex)
        return
      }

      if (this.preview) {
        this.recipient.completed_at = new Date().toISOString()
      } else {
        api
          .patchRecipientDone(this.recipient.token)
          .then((r) => {
            this.upsertRecipient(r.data.recipient)
          })
          .finally(() => {
            // Go to the survey complete page with a full page reload
            this.goToCompletePage()
          })
      }
    },
    buildSlideList() {
      // NOTE: this.slides already have a default value, but Storybook can't see it
      // for some reason, so we set it here as well.
      this.slides = []
      this.slides.push({ type: 'Title' })
      if (this.initiallyShowCompanyQuestion) this.slides.push({ type: 'CompanyQuestion' })
      if (this.initiallyShowRecipientQuestion) this.slides.push({ type: 'RecipientQuestion' })

      this.indexOfFirstQuestionSection = this.slides.length

      this.survey.questions.forEach((q) => {
        this.slides.push({ type: 'SurveyQuestion', question: q })
      })

      this.slides.push({ type: 'EndSurvey' })
    },
    setupScroll() {
      if (this.surveyCompleted) return

      this.fullpageObject = new fullpage('#questions', {
        autoScrolling: true,
        licenseKey: process.env.VUE_APP_FULLPAGE_JS_TOKEN,
        keyboardScrolling: true,
        credits: { enabled: false },
        scrollingspeed: 300,
        touchSensitivity: 15,
        scrollOverflow: true,
        beforeLeave: (origin, destination) => {
          // If not preview &
          // is not on the first slide &
          // is trying to go to next section, validate
          if (!this.preview && origin.index > 0 && destination.index > origin.index) {
            if (!this.questionIsValid()) return false
          }
          return true
        },
        onLeave: (_origin, destination, direction) => {
          const nextSlideOffset = this.logician.nextSlideOffset(destination.index, direction)
          const updatedDestinationIndex = destination.index + nextSlideOffset
          // fullpage.js' moveTo is 1-indexed which is weird and confusing!
          const oneBasedDestinationIndex = updatedDestinationIndex + 1
          if (updatedDestinationIndex !== destination.index) {
            // This setTimeout is necessary to prevent a callstack error
            setTimeout(() => {
              this.fullpageObject.moveTo(oneBasedDestinationIndex)
            })
          }
          this.currentSlide = oneBasedDestinationIndex // see comment above about maybe not tracking this separately
        },
      })

      this.moveTo(this.initialQuestionIndex)
    },
    next() {
      this.fullpageObject.moveSectionDown()
    },
    previous() {
      this.fullpageObject.moveSectionUp()
    },
    moveTo(slideIndex) {
      this.fullpageObject.moveTo(slideIndex + 1) // fullpage.js' moveTo is 1-indexed
    },
    questionIsValid() {
      const question = this.currentQuestion
      if (question) {
        useValidationStore().validate(question.id)
        return !useValidationStore().hasErrors(question.id)
      }

      return true
    },
    getFakeIdForQuestion(questionId) {
      const existingId = this.previewFakeResponseIds.get(questionId)

      if (!existingId || this.currentQuestion?.type === 'MultipleChoiceMany') {
        const randomId = this.getRandomId()
        this.previewFakeResponseIds.set(questionId, randomId)
        return randomId
      }

      return existingId
    },
    saveResponse(responseData) {
      // @TODO remove this if block
      if (this.preview) {
        // Adding a Fake ID to the object so it doesn't get overwritten in the store.
        responseData.id = this.getFakeIdForQuestion(responseData.question_id)
        this.upsertSurveyResponse(responseData)
        return
      }

      // The question ID is needed for the client-side store, but not the backend API
      // (Responses are associated with questions through their Answer)
      delete responseData.question_id

      let action
      if (responseData.response_id) {
        responseData['id'] = responseData.response_id
        action = patchResponse(responseData)
      } else {
        action = postResponse(responseData)
      }

      action
        .then((res) => {
          this.upsertSurveyResponse(res.data.response)
        })
        .catch((e) => {
          appsignal.sendError(e, (span) => {
            span.setAction('upsertResponse')
            span.setNamespace('survey')
            span.setParams({ ...responseData })
          })
        })
    },
    removeResponse(response) {
      if (this.preview) {
        this.removeSurveyResponse(response)
        return
      }

      deleteResponse(response).then(() => {
        this.removeSurveyResponse(response)
      })
    },
  },
}
</script>
<style lang="sass" scoped>
.survey_header
  position: fixed
  width: 100%
  height: 130px
  top: 0
  left: 0
  padding: 15px 20px
  background-color: #FFFFFF
  z-index: 100

  figure
    text-align: left
  h1
    font-size: 1.5rem
    margin-bottom: 0

.footer
  position: fixed
  width: 100%
  height: 60px
  bottom: 0
  left: 0
  padding: 0 30px
  justify-content: space-between
  background-color: #FFFFFF
  z-index: 100
  img
    height: 24px
  .responsive-controls
    display: none

.the_survey
  .company_question, .recipient_question
    width: 100%
  ::v-deep h3, h6
    font-family: Inter
    font-weight: 400
  ::v-deep h3
    font-size: 1.5rem
  ::v-deep .answers
    h6
      color: hsl(200, 12%, 44%)
      font-size: 10px
      font-weight: 200
      text-transform: uppercase
      letter-spacing: 2px
      margin: 32px 0 20px

.questions
  .section
    opacity: 0
    transition: opacity 1s ease-in-out
    position: relative
    .disclaimer
      position: fixed
      left: 0
      bottom: 60px
      padding: 0 30px
      color: hsl(200, 12%, 44%)
      font-size: 12px
    &.active
      opacity: 1
    .content
      width: 75vw
      max-width: 900px
      min-width: 300px
      // slightly lower than the header
      // slightly higher than the footer
      margin: 80px auto 110px auto
    .question-number
      min-width: 40px
      padding-right: 5px
      text-align: right
  .survey-question-section
    padding-top: 60px

.centralize
  height: 100vh
  width: 100vw
  padding: 2em
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center

.survey-expired-text
  max-width: 860px

@media screen and (max-width: 500px)
  .questions
    .section
      .content
        width: 85vw
        padding-right: 20px
        .controls
          display: none
      .question-number
        min-width: auto
      .disclaimer
        position: unset
        padding: 0
        margin-top: 2em
    .survey-question-section
      padding-top: 0px
  .regular-controls, .validation-errors
    display: none
  .done-btn
    display: none
  .footer
    flex-direction: column
    justify-content: end
    .responsive-controls
      background-color: #FFFFFF
      width: 100%
      display: flex
      justify-content: center
      opacity: 1
      transition: opacity 0.15s ease-in-out
      button
        width: 100%
    .credits
      margin: 15px 0
      img
        height: 18px
    .nav_buttons
      display: none !important

  ::v-deep.the_survey
    h3
      font-weight: 400
      font-size: 1.25rem
      line-height: 1.25

::v-deep *
  scrollbar-width: thin

// NOTE: Used .hide with ":class" directive insted of "v-if"
// directly so that we can apply transition effects
.hide
  opacity: 0 !important
</style>
